import * as THREE from 'three';
import type { CameraPreset } from './types';

interface CameraFrameSize {
  width: number;
  height: number;
  length: number;
}

interface CameraPresetConfig {
  id: CameraPreset['id'];
  label: string;
  direction: readonly [number, number, number];
  distanceMultiplier: number;
  targetOffset: readonly [number, number, number];
  /**
   * Absolute plan-view aim point in metres (world X/Z), overriding the X and Z
   * fractions of `targetOffset`. The interior presets use it to look at the
   * lavar the layout actually places rather than at a fixed fraction of the
   * building — the two stop agreeing the moment a user moves a bench.
   *
   * Y is deliberately NOT overridable: eye height frames the room, and tying it
   * to the seat would drop the camera under the ceiling on a foot lav.
   */
  targetPlanM?: { xM: number; zM: number };
}

function getFrameVector(size: CameraFrameSize) {
  return new THREE.Vector3(size.width, size.height, size.length);
}

export function createCameraPreset(
  size: CameraFrameSize,
  config: CameraPresetConfig,
): CameraPreset {
  const frame = getFrameVector(size);
  const target = new THREE.Vector3(
    config.targetPlanM ? config.targetPlanM.xM : size.width * config.targetOffset[0],
    size.height * config.targetOffset[1],
    config.targetPlanM ? config.targetPlanM.zM : size.length * config.targetOffset[2],
  );
  const position = target.clone().add(
    new THREE.Vector3(...config.direction)
      .normalize()
      .multiplyScalar(frame.length() * config.distanceMultiplier),
  );

  return {
    id: config.id,
    label: config.label,
    position,
    target,
  };
}

export function createCameraPosition(
  size: CameraFrameSize,
  direction: readonly [number, number, number],
  distanceMultiplier: number,
  targetOffset: readonly [number, number, number],
) {
  const preset = createCameraPreset(size, {
    id: 'hero',
    label: 'Hero',
    direction,
    distanceMultiplier,
    targetOffset,
  });

  return {
    position: preset.position,
    target: preset.target,
  };
}
